import { updateProject } from "@/components/common/project-provider/update-project";
import { useViewRuntimeContext } from "@/components/common/view-runtime-context";
import { createAnnotationFields } from "@/components/ui/annotations/annotation-fields";
import {
  AnnotationCreationData,
  createPostTopic,
  ExternalAnnotationData,
  isExternalAnnotationData,
  SphereXGAnnotationData,
} from "@/components/ui/annotations/annotation-props";
import { createAttachments } from "@/components/ui/annotations/attachment-mutations";
import {
  addDocumentReferenceToAnnotation,
  getExternalAnnotationProviderName,
  getIElementTypeForBcfIntegrationType,
} from "@/components/ui/annotations/external-annotation-utils";
import {
  applyAsyncMutations,
  useUpdateStoreAfterMutations,
  useUpdateStoreFromMutations,
} from "@/components/ui/annotations/mutations-optimistic-update";
import { useErrorHandlers } from "@/errors/components/error-handling-context";
import { createEmbeddedShareLink } from "@/features/saved-views/capture-saved-view";
import { useCurrentArea } from "@/modes/mode-data-context";
import { addBackgroundTask } from "@/store/background-tasks/background-tasks-slice";
import { selectProjectIdForIntegrationType } from "@/store/integrations/integrations-selectors";
import {
  useAppDispatch,
  useAppSelector,
  useAppStore,
} from "@/store/store-hooks";
import { selectCurrentUser } from "@/store/user-selectors";
import { MutationTask, MutationTaskType } from "@/utils/background-tasks";
import { assert, generateGUID, GUID } from "@faro-lotv/foundation";
import {
  isIElementAreaSection,
  isIElementBimModelSection,
  isIElementModel3dStream,
} from "@faro-lotv/ielement-types";
import {
  IElementWithPose,
  selectAdvancedMarkupTemplateIds,
  selectAncestor,
  selectIElement,
  selectProjectId,
} from "@faro-lotv/project-source";
import {
  BackgroundTaskState,
  createMutationAddCamView,
  createMutationAddExternalMarkup,
  createMutationAddLabel,
  createMutationAddMarkup,
  createMutationAddMarkupPolygon,
  Mutation,
  useApiClientContext,
} from "@faro-lotv/service-wires";
import { Camera } from "@react-three/fiber";
import { DateTime } from "luxon";
import { useCallback } from "react";
import { OrthographicCamera, PerspectiveCamera } from "three";
import {
  AnnotationCameraParameters,
  selectMutationAddCamViewData,
} from "../annotation-camview-utils";
import {
  PointAnnotationData,
  selectAddPolygonMutationData,
} from "../annotation-mutation-utils";

/**
 * Defines the type of the function that will be used for the creation of
 * multiple single point annotations (mix of either Sphere XG or external).
 */
type CreateSinglePointAnnotation = (
  annotation: PointAnnotationData,
  details: AnnotationCreationData,
  cameraParams: AnnotationCameraParameters | undefined,
) => Promise<GUID>;

/**
 * @returns a function to use for the creation of Sphere XG single point annotation or
 * for external single point annotation. The returned function will throw an error if
 * the creation of the annotation fails.
 * REMARKS: Sphere XG annotations can be created in batch with useCreateSphereSinglePointAnnotations
 * in a more efficient way than calling useCreateSinglePointAnnotation multiple times.
 */
export function useCreateSinglePointAnnotation(): CreateSinglePointAnnotation {
  const createExternalSinglePointAnnotation =
    useCreateExternalMarkupSinglePointAnnotation();
  const createSphereSinglePointAnnotations =
    useCreateSphereSinglePointAnnotations();
  return useCallback<CreateSinglePointAnnotation>(
    async (
      annotation: PointAnnotationData,
      details: AnnotationCreationData,
      cameraParams: AnnotationCameraParameters | undefined,
    ) => {
      if (isExternalAnnotationData(details)) {
        return createExternalSinglePointAnnotation(
          annotation,
          details,
          cameraParams,
        );
      }
      const guids = await createSphereSinglePointAnnotations([
        { annotation, details, cameraParams },
      ]);
      return guids[0];
    },
    [createExternalSinglePointAnnotation, createSphereSinglePointAnnotations],
  );
}

export type CreateSphereSinglePointAnnotationParams = {
  annotation: PointAnnotationData;
  details: SphereXGAnnotationData;
  cameraParams: AnnotationCameraParameters | undefined;
  /* The id of the new group created by first annotation when 2+ annotations are created at the same time and there was no group. */
  newGroupId?: GUID;
};

/**
 * Defines the type of the function that will be used for the creation of
 * multiple single point annotations (mix of either Sphere XG or external).
 */
type CreateSphereSinglePointAnnotations = (
  params: CreateSphereSinglePointAnnotationParams[],
) => GUID[];

/**
 * @returns a function to use for the creation of multiple Sphere XG single point annotations.
 * The returned function will throw an error if the creation of any annotation fails.
 */
export function useCreateSphereSinglePointAnnotations(): CreateSphereSinglePointAnnotations {
  const createSphereSinglePointAnnotationMutations =
    useCreateSphereSinglePointAnnotationMutations();
  const { projectApiClient } = useApiClientContext();
  const { handleErrorWithToast } = useErrorHandlers();

  const updateStoreAfterMutations = useUpdateStoreAfterMutations();

  return useCallback<CreateSphereSinglePointAnnotations>(
    (params: CreateSphereSinglePointAnnotationParams[]): GUID[] => {
      const allSphereAnnotationMutations: Mutation[] = [];
      const annotations: GUID[] = [];
      const taskData: Array<{
        taskId: GUID;
        targetIElementId: GUID;
      }> = [];
      params.forEach((param) => {
        // Sphere XG annotations will be created in one single request
        const sphereAnnotationMutationsParams =
          createSphereSinglePointAnnotationMutations({
            ...param,
            details: { ...param.details },
          });
        allSphereAnnotationMutations.push(
          ...sphereAnnotationMutationsParams.mutations,
        );
        annotations.push(sphereAnnotationMutationsParams.markupId);

        taskData.push({
          taskId: sphereAnnotationMutationsParams.taskId,
          targetIElementId: sphereAnnotationMutationsParams.ancestorId,
        });
      });

      // Create all Sphere XG annotations in a single batch
      if (allSphereAnnotationMutations.length) {
        applyAsyncMutations(projectApiClient, allSphereAnnotationMutations)
          .catch((error) => {
            handleErrorWithToast({
              title: "Could not create Annotation",
              error,
            });
          })
          .finally(() => {
            updateStoreAfterMutations(taskData);
          });
      }

      return annotations;
    },
    [
      createSphereSinglePointAnnotationMutations,
      projectApiClient,
      handleErrorWithToast,
      updateStoreAfterMutations,
    ],
  );
}

/**
 * Defines the type of data returned
 * by CreateSphereSinglePointAnnotationMutations function
 */
type SinglePointAnnotationMutationsOutput = {
  /* The ID of the new markup/annotation element */
  markupId: GUID;
  /* The ID of the ancestor element of markupId (to be used for updating the local elements) */
  ancestorId: GUID;
  /* The list of mutations to be applied for creating the new annotation */
  mutations: Mutation[];
  /** The id of the associated background task */
  taskId: GUID;
};

/**
 * Defines the type of the function that should be used for the allocation of mutations for creating a
 * Sphere XG single point annotations
 */
type CreateSphereSinglePointAnnotationMutations = (
  params: CreateSphereSinglePointAnnotationParams,
) => SinglePointAnnotationMutationsOutput;

/**
 * @returns a function to allocate the mutations required for the creation of Sphere XG single point annotation.
 */
function useCreateSphereSinglePointAnnotationMutations(): CreateSphereSinglePointAnnotationMutations {
  const projectId = useAppSelector(selectProjectId);
  const currentUser = useAppSelector(selectCurrentUser);
  const { getState } = useAppStore();
  const dispatch = useAppDispatch();

  const updateStoreFromMutations = useUpdateStoreFromMutations();

  return useCallback<CreateSphereSinglePointAnnotationMutations>(
    (params: CreateSphereSinglePointAnnotationParams) => {
      const {
        title,
        assignee,
        description,
        dueDate,
        status,
        newAttachments,
        tags,
      } = params.details;
      function createAnnotationMutations(): SinglePointAnnotationMutationsOutput {
        assert(currentUser, "Expected a logged in user");
        assert(projectId, "Expected a project ID");

        const markupPolygonId = generateGUID();

        // Find the parent area of the element (if it exists), or active area in case of CAD model
        const referenceElement = selectIElement(params.annotation.parentId)(
          getState(),
        );
        assert(
          referenceElement,
          "Reference element for the annotation does not exist",
        );
        const annotationParentSection = selectAncestor(
          referenceElement,
          isIElementModel3dStream(referenceElement)
            ? isIElementBimModelSection
            : isIElementAreaSection,
        )(getState());

        const markupMutationData = selectAddPolygonMutationData(
          params.annotation,
          annotationParentSection,
        )(getState());
        assert(
          markupMutationData,
          "Unable to compute the data to store for the new annotation",
        );

        const { mutationData, worldMatrixInverse } = markupMutationData;

        const addMarkupPolygonMutation = createMutationAddMarkupPolygon({
          ...mutationData,
          groupId: mutationData.groupId,
          newElementId: markupPolygonId,
          name: title,
          typeHint: params.annotation.type,
          acquisitionPanoId: params.annotation.acquisitionPanoId,
        });

        const markupId = generateGUID();
        const templateIds = selectAdvancedMarkupTemplateIds(getState());
        assert(
          templateIds,
          "Expected project to have an advanced markup template",
        );

        const markupFields = createAnnotationFields({
          assignee,
          status,
          dueDate,
          ...templateIds,
          currentUserId: currentUser.id,
          markupId,
          rootId: mutationData.rootId,
        });

        const addMarkupMutation = createMutationAddMarkup({
          id: markupId,
          templateId: templateIds.templateId,
          rootId: mutationData.rootId,
          name: title,
          description: description ?? "",
          annotationId: markupPolygonId,
          markupFields,
        });

        const attachmentsMutations = createAttachments(
          mutationData.rootId,
          markupId,
          newAttachments,
        );

        const tagsMutations =
          tags?.map((tag) => createMutationAddLabel(markupId, tag.id)) ?? [];

        const extras: IElementWithPose[] = addMarkupPolygonMutation.group
          ? [addMarkupPolygonMutation.group]
          : [];
        extras.push(addMarkupPolygonMutation.newElement);
        const camViewMutationData = params.cameraParams
          ? selectMutationAddCamViewData(
              params.cameraParams,
              markupPolygonId,
              mutationData.rootId,
              worldMatrixInverse,
              extras,
            )(getState())
          : null;

        const camViewMutation = camViewMutationData
          ? createMutationAddCamView(camViewMutationData)
          : null;

        const mutations: Mutation[] = [
          { ...addMarkupPolygonMutation, group: undefined },
          addMarkupMutation,
          ...attachmentsMutations,
          ...tagsMutations,
        ];
        if (camViewMutation) mutations.push(camViewMutation);

        const isoDate = DateTime.now().toISO();
        const task: MutationTask = {
          type: "Mutation",
          id: generateGUID(),
          iElementId: addMarkupMutation.newElement.id,
          rootId: mutationData.sectionId,
          createdAt: isoDate,
          changedAt: isoDate,
          devMessage: "Creating a point annotation",
          state: BackgroundTaskState.created,
          progress: 1,
          shouldPreventWindowClose: true,
          mutationType: MutationTaskType.CreateAnnotation,
        };
        dispatch(addBackgroundTask(task));

        // Compute the sub-tree hierarchy of iElements that should be added or
        // updated in the store
        updateStoreFromMutations([
          addMarkupPolygonMutation,
          addMarkupMutation,
          ...tagsMutations,
          ...attachmentsMutations,
          ...(camViewMutation ? [camViewMutation] : []),
        ]);

        return {
          markupId,
          ancestorId: mutationData.sectionId,
          mutations,
          taskId: task.id,
        };
      }

      return createAnnotationMutations();
    },
    [currentUser, dispatch, getState, projectId, updateStoreFromMutations],
  );
}

/**
 * Defines the type of the function that should be used for the creation of
 * external single point annotations
 */
type CreateExternalSinglePointAnnotation = (
  annotation: PointAnnotationData,
  details: ExternalAnnotationData,
  cameraParams: AnnotationCameraParameters | undefined,
) => Promise<GUID>;

/**
 * @returns a function to use for the creation of external single point annotation.
 * The returned function will throw an error if the creation of the annotation fails.
 */
function useCreateExternalMarkupSinglePointAnnotation(): CreateExternalSinglePointAnnotation {
  const dispatch = useAppDispatch();
  const { bcfServicesApiClient, projectApiClient } = useApiClientContext();
  const appStore = useAppStore();
  const { area } = useCurrentArea();
  const viewContext = useViewRuntimeContext();

  return useCallback<CreateExternalSinglePointAnnotation>(
    (annotation, details, cameraParams) => {
      async function createAnnotation(): Promise<GUID> {
        const appState = appStore.getState();
        const markupPolygonId = generateGUID();

        assert(area, "Expected an area to be defined");

        const markupMutationData = selectAddPolygonMutationData(
          annotation,
          area,
        )(appState);
        assert(
          markupMutationData,
          "Unable to compute the data to store for the new annotation",
        );
        const { mutationData, worldMatrixInverse } = markupMutationData;

        const externalMarkupId = generateGUID();
        const projectId = selectProjectId(appState);
        assert(projectId, "Expected a project ID");
        const deepLinkUrl = (
          await createEmbeddedShareLink(projectId, appState, viewContext, {
            lookAtId: externalMarkupId,
            includeContext: true,
          })
        ).toString();
        const { externalAnnotationType, screenshotFile } = details;

        const postTopic = createPostTopic({
          data: details,
          id: externalMarkupId,
          deepLinkUrl,
          providerName: getExternalAnnotationProviderName(
            externalAnnotationType,
          ),
        });

        const integrationProjectId = selectProjectIdForIntegrationType(
          externalAnnotationType,
        )(appState);
        assert(
          integrationProjectId,
          "integration project id should be defined",
        );
        const getTopic = await bcfServicesApiClient.createTopic(
          externalAnnotationType,
          integrationProjectId,
          postTopic,
        );

        const addExternalMarkupMutation = createMutationAddExternalMarkup({
          id: externalMarkupId,
          type: getIElementTypeForBcfIntegrationType(externalAnnotationType),
          externalIssueId: getTopic.guid,
          parentId: markupPolygonId,
          rootId: mutationData.rootId,
        });

        const addMarkupPolygonMutation = createMutationAddMarkupPolygon({
          ...mutationData,
          newElementId: markupPolygonId,
          name: getTopic.guid,
          typeHint: annotation.type,
          acquisitionPanoId: annotation.acquisitionPanoId,
        });

        const extras: IElementWithPose[] = addMarkupPolygonMutation.group
          ? [addMarkupPolygonMutation.group]
          : [];
        extras.push(addMarkupPolygonMutation.newElement);
        const camViewMutationData = cameraParams
          ? selectMutationAddCamViewData(
              cameraParams,
              markupPolygonId,
              mutationData.rootId,
              worldMatrixInverse,
              extras,
            )(appState)
          : null;

        const camViewMutation = camViewMutationData
          ? createMutationAddCamView(camViewMutationData)
          : null;

        const mutations: Mutation[] = [
          { ...addMarkupPolygonMutation, group: undefined },
          addExternalMarkupMutation,
        ];
        if (camViewMutation) mutations.push(camViewMutation);

        await projectApiClient.applyMutations(mutations);

        if (screenshotFile) {
          await addDocumentReferenceToAnnotation(
            bcfServicesApiClient,
            externalAnnotationType,
            integrationProjectId,
            getTopic.guid,
            screenshotFile,
          );
        }

        await dispatch(
          updateProject({
            projectApi: projectApiClient,
            iElementQuery: {
              ancestorIds: [mutationData.sectionId],
            },
          }),
        );

        return externalMarkupId;
      }
      return createAnnotation();
    },
    [
      appStore,
      area,
      bcfServicesApiClient,
      dispatch,
      projectApiClient,
      viewContext,
    ],
  );
}

/**
 * @returns Camera parameters to be stored with the annotation
 * @param camera the camera from which the annotation was created
 */
export function cameraToAnnotationCameraParams(
  camera: Camera,
): AnnotationCameraParameters {
  let zoomLevel = 0;
  let perspective = true;
  if (camera instanceof PerspectiveCamera) {
    zoomLevel = camera.getEffectiveFOV();
  } else if (camera instanceof OrthographicCamera) {
    perspective = false;
    zoomLevel = (camera.top - camera.bottom) / camera.zoom;
  }
  return {
    cameraPose: camera.matrixWorld,
    perspective,
    zoomLevel,
  };
}
